import React, { Fragment, Component } from "react";
import { withRouter } from "next/router";
import PropTypes from "prop-types";
import { COLORS } from "utils/styles";
import compact from "lodash/compact";
import CounterBubble from "components/CounterBubble";
import classnames from "classnames";
import { getActiveTabID, pathPrefixWithoutTabID } from "utils/tabs";
import merge from "lodash/merge";
import { MAX_WIDTH } from "utils/constants/ui";
import { getRouteParamPaths, extractQSFromAsPath } from "utils/route";

const TAB_HEIGHT = 42;

export const WHITE_TAB_STYLES = {
  tab: {
    hover: {
      active: {
        backgroundColor: COLORS.white
      }
    },
    noHover: {
      active: {
        backgroundColor: COLORS.white
      }
    }
  }
};

export const WHITE_TAB_PROPS = {
  customStyles: WHITE_TAB_STYLES,
  customClasses: {
    tab: {
      first: "ml3",
      last: "mr3"
    }
  }
};

const COMMON_WHITE_TAB_PROPS = {
  minWidth: false,
  equalWidth: false,
  pathPrefix: "",
  customStyles: WHITE_TAB_STYLES
};

const TABS_LIST_CLASSES =
  "list ma0 pa0 nowrap center overflow-x-auto absolute top-0 left-0 right-0 ph3";

export const TABS_PROPS = {
  ...COMMON_WHITE_TAB_PROPS,
  customClasses: {
    tabs: {
      list: TABS_LIST_CLASSES
    }
  }
};
export const BLOCK_SETTINGS_TABS_PROPS = {
  ...COMMON_WHITE_TAB_PROPS,
  customClasses: {
    tabs: {
      list: `${TABS_LIST_CLASSES} bh--inset`
    }
  }
};
export const getTabsProps = (pathPrefix) => ({
  minWidth: false,
  equalWidth: false,
  pathPrefix: pathPrefix || "",
  customStyles: WHITE_TAB_STYLES,
  customClasses: {
    tabs: {
      list: TABS_LIST_CLASSES
    }
  }
});
const MINI_TAB_HEIGHT = 24;

export const MINI_TABS_PROPS = {
  minWidth: false,
  equalWidth: false,
  pathPrefix: "",
  customStyles: {
    tab: {
      hover: {
        active: {
          height: MINI_TAB_HEIGHT,
          backgroundColor: "white",
          borderBottom: `1px solid ${COLORS.blue}`
        },
        inactive: {
          height: MINI_TAB_HEIGHT,
          backgroundColor: "white",
          borderBottom: `1px solid ${COLORS.deactivated}`
        }
      },
      noHover: {
        active: {
          height: MINI_TAB_HEIGHT,
          backgroundColor: "white",
          borderBottom: `1px solid ${COLORS.blue}`
        },
        inactive: {
          height: MINI_TAB_HEIGHT,
          backgroundColor: "white",
          borderBottom: `1px solid white`
        }
      }
    }
  },
  customClasses: {
    tabs: {
      container: "w-100 bg-white relative",
      list:
        "list ma0 ph0 nowrap center overflow-x-auto absolute top-0 left-0 right-0 bb hairline-1"
    },
    tab: {
      container: "dib tc pointer pb2",
      label: "f5 relative",
      active: "fw6",
      first: "ma0",
      last: "ma0"
    }
  }
};

const PRIMARY_TAB_STYLES = {
  hover: {
    active: {
      height: TAB_HEIGHT + 2,
      backgroundColor: COLORS.bg,
      borderBottom: "none"
    },
    inactive: {
      height: TAB_HEIGHT - 1,
      backgroundColor: COLORS.tabSecondaryHover,
      borderBottom: `1px solid #E5E5E5`
    }
  },
  noHover: {
    active: {
      height: TAB_HEIGHT + 2,
      backgroundColor: COLORS.bg
    },
    inactive: {
      height: TAB_HEIGHT - 1,
      backgroundColor: "transparent"
    }
  }
};

const DEFAULT_CLASSES = {
  tabs: {
    container: "w-100 bg-white bb b--black-10 relative",
    list:
      "list ma0 pa0 nowrap center overflow-x-auto absolute top-0 left-0 right-0"
  },
  content: {
    container: "w-100 center"
  },
  tab: {
    container: "dib tc pointer pb1",
    wrapper: "ph4 ws-normal h-100 flex items-center",
    label: "f5 relative",
    active: "bl bt br b--black-10 fw6",
    first: "ml3 ml0-l",
    last: "mr3 mr0-l"
  }
};

const DEFAULT_STYLES = {
  tab: PRIMARY_TAB_STYLES,
  content: {
    maxWidth: MAX_WIDTH
  }
};

const BUBBLE_STYLES = {
  top: -5,
  left: -26,
  height: 24,
  width: 24,
  fontSize: 12
};

/**
 * Tabs component is synced to the router state so that
 * multiple tabs can be set to active depending on the tab query string param path
 * Review the tests to understand the logic for choosing the active tab id
 * and the logic for ensuring the pathPrefix used doesnt include any of the tab ids as its last path fragment
 */
class Tabs extends Component {
  constructor(props) {
    super(props);
    const { customStyles, customClasses } = props;

    this.updateIndex = this.updateIndex.bind(this);
    this.setActiveTabId = this.setActiveTabId.bind(this);
    this.updateTabsScrollPosition = this.updateTabsScrollPosition.bind(this);

    this._tabRefs = [];
    this._tabsList = null;
    this._styles = merge({}, DEFAULT_STYLES, customStyles);
    this._classes = merge({}, DEFAULT_CLASSES, customClasses);

    const activeTabID =
      props.activeTabID ||
      getActiveTabID(
        props.tabs,
        this.pathPrefix(props),
        extractQSFromAsPath(props.router.asPath).tab
      );

    this.state = {
      activeId: activeTabID
    };
  }

  componentDidMount() {
    this.updateTabsScrollPosition();
  }

  /**
   * Ensure pathPrefix does not have a tab id as its last path fragment
   * - If it did then there would be a concatenation of tab paths
   * - And tab toggle active states would no longer work
   * @param {*} props
   */
  pathPrefix(props) {
    const ctxProps = props || this.props;
    const { pathPrefix, tabs } = ctxProps;

    return pathPrefix && typeof pathPrefix === "string"
      ? pathPrefixWithoutTabID(pathPrefix, tabs)
      : "";
  }

  componentDidUpdate(prevProps) {
    const { tabs: prevTabs, router: prevRouter } = prevProps;
    const { tabs, router } = this.props;
    const tabsChanged = prevTabs.length !== tabs.length;
    const prevTab = extractQSFromAsPath(prevRouter.asPath).tab;
    const currentTab = extractQSFromAsPath(router.asPath).tab;
    const queryParamChanged = prevTab !== currentTab;

    if (tabsChanged || queryParamChanged) {
      this.setActiveTabId();
    }
  }

  setActiveTabId() {
    const activeTabID = getActiveTabID(
      this.props.tabs,
      this.pathPrefix(),
      extractQSFromAsPath(this.props.router.asPath).tab
    );

    if (activeTabID !== this.state.activeTabID) {
      this.setState(
        {
          activeId: activeTabID
        },
        this.updateTabsScrollPosition
      );
    }
  }

  updateTabsScrollPosition() {
    const { activeId } = this.state;
    const tabRefIndex = this._tabRefs.findIndex(
      (tabRef) => tabRef.id === activeId
    );
    const paddingAmount = 30;
    const tabsCanScroll =
      this._tabsList && typeof this._tabsList.scrollTo === "function";

    if (tabRefIndex >= 0 && tabsCanScroll) {
      const ctxTab = this._tabRefs[tabRefIndex];
      const ctxTabWidth = ctxTab.ref.getBoundingClientRect().width;
      const ctxTabPosX = ctxTab.ref.getBoundingClientRect().x;
      const pageWidth = document.body.getBoundingClientRect().width;
      const tabRightEdge = ctxTabPosX + ctxTabWidth;
      const rightOffset = pageWidth - tabRightEdge;
      const isVisibleRight = rightOffset > 0;

      const preceedingTabs = this._tabRefs.slice(0, tabRefIndex);
      const tabDisplacement = preceedingTabs
        .filter((tab) => Boolean(tab.ref))
        .reduce((memo, tab) => {
          memo += tab.ref.getBoundingClientRect().width;
          return memo;
        }, 0);

      if (!isVisibleRight) {
        const newScroll =
          tabDisplacement - pageWidth + ctxTabWidth + paddingAmount;
        this._tabsList.scrollTo(newScroll, 0);
      }
      const isVisibleLeft = ctxTabPosX > 0;

      if (!isVisibleLeft) {
        this._tabsList.scrollTo(tabDisplacement, 0);
      }
    }
  }

  /**
   * Set the chosen tab in internal state
   * Update the route state with the chosen tab id
   * - shallow render so its not a full page refresh
   * @param {string} chosenTabId - context tab id chosen
   */
  updateIndex(chosenTabId) {
    const { onUpdate, router } = this.props;

    const { uuid, ...remainder } = extractQSFromAsPath(router.asPath);

    const params = {
      ...remainder,
      tab: compact([this.pathPrefix(), chosenTabId]).join(".")
    };

    const routePaths = getRouteParamPaths(router, params);

    this.setState({ activeId: chosenTabId }, () => {
      router.push(routePaths.pathname, routePaths.asPath);

      onUpdate && onUpdate(chosenTabId);
    });
  }

  tabNodes(activeId) {
    const { tabs, beforeUpdate } = this.props;

    if (tabs.length > 1) {
      const tabNodes = tabs.map(({ name, id, count }, tabIx) => {
        const isFirst = !tabIx;
        const isLast = tabIx === tabs.length - 1;
        const isActive = activeId === id;

        return (
          <li
            ref={(tabRef) => this._tabRefs.push({ id, ref: tabRef })}
            key={tabIx}
            className={classnames(`tab--item ${this._classes.tab.container}`, {
              [this._classes.tab.active]: isActive,
              [this._classes.tab.first]: isFirst,
              [this._classes.tab.last]: isLast
            })}
            onClick={() => {
              beforeUpdate && beforeUpdate(id);

              setTimeout(() => this.updateIndex(id), 0);
            }}
          >
            <div className={this._classes.tab.wrapper}>
              <div className={this._classes.tab.label}>
                {name}
                {Boolean(count) && (
                  <CounterBubble count={count} customStyles={BUBBLE_STYLES} />
                )}
              </div>
            </div>
            <style jsx>
              {`
                .tab--item {
                  height: ${isActive
                    ? this._styles.tab.noHover.active.height
                    : this._styles.tab.noHover.inactive.height}px;
                  background-color: ${isActive
                    ? this._styles.tab.noHover.active.backgroundColor
                    : this._styles.tab.noHover.inactive.backgroundColor};
                  border-bottom: ${isActive
                    ? this._styles.tab.noHover.active.borderBottom
                    : this._styles.tab.noHover.inactive.borderBottom};
                }
                .tab--item:hover {
                  height: ${isActive
                    ? this._styles.tab.hover.active.height
                    : this._styles.tab.hover.inactive.height}px !important;
                  background-color: ${isActive
                    ? this._styles.tab.hover.active.backgroundColor
                    : this._styles.tab.hover.inactive.backgroundColor};
                  border-bottom: ${isActive
                    ? this._styles.tab.hover.active.borderBottom
                    : this._styles.tab.hover.inactive.borderBottom};
                }
              `}
            </style>
          </li>
        );
      });

      return (
        <div
          className={this._classes.tabs.container}
          style={{ paddingTop: this._styles.tab.noHover.active.height - 2 }}
        >
          <ul
            ref={(tabsList) => (this._tabsList = tabsList)}
            className={this._classes.tabs.list}
            style={this._styles.content}
          >
            {tabNodes}
          </ul>
        </div>
      );
    }
  }

  render() {
    const { tabs, mountInactive } = this.props;
    const { activeId } = this.state;

    const content = tabs
      .filter((tab) => Boolean(tab.node))
      .map((tab, tabIx) => {
        const activeTab = tab.id === activeId;
        /**
         * Optionally choose to NOT mount inactive tabs for perf reasons
         */
        const dontMountInactive = !mountInactive && !activeTab;
        if (dontMountInactive) {
          return;
        }

        let tabProps = {};
        if (typeof tab.node.type === "function") {
          tabProps = {
            tabPathPrefix: compact([this.pathPrefix(), tab.id]).join(".")
          };
        }
        // Update to handle node function?
        // const node = typeof tab.node === "function" ? tab.node() : tab.node;

        return (
          <div
            key={tabIx}
            className={classnames(this._classes.content.container, {
              dn: mountInactive && !activeTab
            })}
            style={this._styles.content}
          >
            {React.cloneElement(tab.node, tabProps)}
          </div>
        );
      });

    return (
      <Fragment>
        {this.tabNodes(activeId)}
        {content}
      </Fragment>
    );
  }
}

Tabs.propTypes = {
  tabs: PropTypes.array,
  onUpdate: PropTypes.func,
  customClasses: PropTypes.shape({
    container: PropTypes.string
  }),
  customStyles: PropTypes.shape({
    tabs: PropTypes.object
  }),
  pathPrefix: PropTypes.string,
  mountInactive: PropTypes.bool
};

Tabs.defaultProps = {
  onUpdate: () => {},
  tabsPrefix: "",
  mountInactive: false
};

export default withRouter(Tabs);
